import { ReactElement, useState, useMemo, useRef } from 'react'
import {
    MainResponses,
    ShowQuickResponses,
    StyledChatInput,
    QuickResponses,
    SubResponses,
} from './styles'
import { Button, SVG, Text } from '../../Atoms'
import { ChatInputProps } from './props'
import { Carousel } from '../../Organisms'
import TextInputSurrounded from '../TextInputSurrounded'
import Flex from '../Flex'
import { SVGs, useDelayUnmount, useOnClickOutside } from '../..'

const ChatInput = ({
    buttonText = 'Send',
    isOpen = false,
    onClickResponse,
    onClickSend,
    quickResponse = [],
    quickText,
    value,
    ...props
}: ChatInputProps): ReactElement => {
    const inputRef = useRef<HTMLInputElement>(null)

    const [dragging, setDragging] = useState(false)
    const [focused, setFocused] = useState(false)
    const [quickResOpen, setQuickResOpen] = useState(isOpen)
    const [selectedResponse, setSelectedResponse] = useState('')

    useOnClickOutside(inputRef, () => setFocused(false))

    const isEmpty = value === ''

    const quickResponses = useMemo(
        () =>
            quickResponse.map((res, key) => {
                const { mainResponse: mainRes } = res

                return (
                    <Button
                        active={mainRes === selectedResponse}
                        key={key}
                        onClick={() => {
                            if (!dragging)
                                setSelectedResponse(
                                    mainRes === selectedResponse ? '' : mainRes
                                )
                        }}
                        rounded
                        variant="option"
                        size="small"
                    >
                        {mainRes}
                    </Button>
                )
            }),
        [dragging, quickResponse, selectedResponse]
    )

    const quickSubResponses = useMemo(
        () =>
            quickResponse
                .find((res) => res.mainResponse === selectedResponse)
                ?.subResponse.map((res, key) => {
                    const { content, heading } = res

                    return (
                        <Button
                            key={key}
                            onClick={() => {
                                onClickResponse?.(content)
                                setSelectedResponse('')
                                setQuickResOpen(false)
                            }}
                            rounded
                            variant="option"
                            size="small"
                        >
                            {heading}
                        </Button>
                    )
                }),
        [onClickResponse, quickResponse, selectedResponse]
    )

    const shouldRender = useDelayUnmount(quickResOpen, 300)

    return (
        <StyledChatInput ref={inputRef}>
            <QuickResponses>
                <ShowQuickResponses
                    onClick={() => {
                        setQuickResOpen(!quickResOpen)
                        setSelectedResponse('')
                    }}
                >
                    <SVG
                        css={{
                            color: '$primary600',
                            size: 24,
                            transform: `rotate(${
                                quickResOpen ? '180deg' : '0deg'
                            })`,
                        }}
                        src={SVGs.ChevronRightCircle}
                    />
                    {!shouldRender && (
                        <Text primary bold>
                            {quickText}
                        </Text>
                    )}
                </ShowQuickResponses>
                <MainResponses
                    css={{
                        transform: `translateX(${
                            quickResOpen ? '0%' : '-110%'
                        })`,
                    }}
                >
                    {quickResponses && (
                        <Carousel
                            afterChange={() => setDragging(false)}
                            initialSlide={0}
                            onSwipe={() => setDragging(true)}
                            slidesToShow={3}
                            speed={300}
                            swipe
                            swipeToSlide
                            variableWidth
                        >
                            {quickResponses}
                        </Carousel>
                    )}
                    {quickSubResponses && (
                        <SubResponses>{quickSubResponses}</SubResponses>
                    )}
                </MainResponses>
            </QuickResponses>
            <Flex gap={8} alignItems="flex-end" justifyContent="space-between">
                <TextInputSurrounded
                    {...props}
                    minRows={1}
                    onFocus={() => setFocused(true)}
                    showText={focused}
                    value={value}
                />
                <Button
                    disabled={isEmpty}
                    variant="success"
                    onClick={onClickSend}
                >
                    {buttonText}
                </Button>
            </Flex>
        </StyledChatInput>
    )
}

export default ChatInput

export { StyledChatInput }
